import React, { useEffect, useState } from 'react';
import API_CONFIG from '../utils/apiConfig';

const EMPTY_PERMS = {
  view_payments: false,
  approve_edit_payments: false,
  make_payments: false,
  add_users: false,
  view_users: false,
  view_subcontractors: false,
  add_subcontractors: false,
};

export default function ProfileUsersTab({ currentUser, canAddUsers, canDeleteUsers }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', phone_number: '', permissions: { ...EMPTY_PERMS } });

  const [editUser, setEditUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getEmployerUsers}?page=1&per_page=100`)
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load users');
      }
      const data = await response.json();
      setUsers(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const togglePerm = (key, source = 'form') => {
    if (source === 'form') {
      setForm((prev) => ({
        ...prev,
        permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
      }));
      return;
    }
    setEditUser((prev) => ({
      ...prev,
      permissions: { ...(prev.permissions || {}), [key]: !(prev.permissions || {})[key] },
    }));
  };

  const handleCreate = async () => {
    if (!form.email) {
      setError('Email is required');
      return;
    }
    setCreateLoading(true);
    setError('');
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.createEmployerUser),
        {
          method: 'POST',
          body: JSON.stringify({ email: form.email, permissions: form.permissions }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to create user');
      }
      setSuccess('User invitation sent successfully.');
      setShowCreate(false);
      setForm({ email: '', full_name: '', phone_number: '', permissions: { ...EMPTY_PERMS } });
      await fetchUsers();
    } catch (e) {
      setError(e.message || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    setEditLoading(true);
    setError('');
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.updateEmployerUser}/${editUser.user_id}`),
        {
          method: 'PUT',
          body: JSON.stringify({
            full_name: editUser.full_name,
            phone_number: editUser.phone_number || null,
            status: editUser.status || 'active',
            permissions: editUser.permissions || {},
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to update user');
      }
      setSuccess('User updated successfully.');
      setEditUser(null);
      await fetchUsers();
    } catch (e) {
      setError(e.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    setError('');
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.deleteEmployerUser}/${userId}`),
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete user');
      }
      setSuccess('User deleted successfully.');
      await fetchUsers();
    } catch (e) {
      setError(e.message || 'Failed to delete user');
    }
  };

  return (
    <div className="form-section">
      {success ? <div className="message success">{success}</div> : null}
      {error ? <div className="message error">{error}</div> : null}

      {canAddUsers ? (
        <div style={{ marginBottom: 12 }}>
          <button type="button" className="change-password-btn" onClick={() => setShowCreate((v) => !v)}>
            {showCreate ? 'Close' : '+ Add User'}
          </button>
        </div>
      ) : null}

      {showCreate ? (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))', gap: 8 }}>
            {Object.keys(EMPTY_PERMS).map((k) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={!!form.permissions[k]} onChange={() => togglePerm(k)} />
                <span>{k.replaceAll('_', ' ')}</span>
              </label>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="save-btn" onClick={handleCreate} disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="loading-state">Loading users...</div>
      ) : (
        <div className="table-container">
          <table className="relationships-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id}>
                  <td>{u.full_name || '-'}</td>
                  <td>{u.email}</td>
                  <td>{u.status}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="view-button" onClick={() => setEditUser({ ...u, permissions: u.permissions || {} })}>
                        Edit
                      </button>
                      {canDeleteUsers && u.user_id !== currentUser?.user_id ? (
                        <button type="button" className="view-button" onClick={() => handleDelete(u.user_id)}>Delete</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editUser ? (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 12, marginTop: 16 }}>
          <h4 style={{ marginTop: 0 }}>Edit User</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Full name</label>
              <input value={editUser.full_name || ''} onChange={(e) => setEditUser((p) => ({ ...p, full_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={editUser.phone_number || ''} onChange={(e) => setEditUser((p) => ({ ...p, phone_number: e.target.value }))} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={editUser.status || 'active'} onChange={(e) => setEditUser((p) => ({ ...p, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))', gap: 8 }}>
            {Object.keys(EMPTY_PERMS).map((k) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={!!(editUser.permissions || {})[k]} onChange={() => togglePerm(k, 'edit')} />
                <span>{k.replaceAll('_', ' ')}</span>
              </label>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="button" className="save-btn" onClick={handleUpdate} disabled={editLoading}>
              {editLoading ? 'Updating...' : 'Update User'}
            </button>
            <button type="button" className="cancel-btn" onClick={() => setEditUser(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {false && users.length > 0 ? (
        <div className="pagination-container" style={{ marginTop: 12 }}>
          <div className="pagination-info">
            Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} results
          </div>
          <div className="pagination-controls">
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }} className="pagination-select">
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
            <button type="button" className="pagination-button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
            <span className="pagination-page-info">Page {page} of {totalPages || 1}</span>
            <button type="button" className="pagination-button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
